import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LEGAL_DOCUMENT_TYPES,
  LegalDocument,
  LegalDocumentType,
  listPublishedLegalDocuments,
} from "../../services/legalDocument.service";
import { useAuth } from "../../hooks/useAuth";
import { LEGAL_CONTENT_CLASSES } from "../../utils/legalContent";
import { formatVersionDisplay } from "../../utils/semver";
import {
  ArrowLeft,
  History,
  LogOut,
  Scale,
  ScrollText,
  ShieldCheck,
  FileText,
} from "lucide-react";

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
      {label}
    </dt>
    <dd className="mt-0.5 text-sm font-medium text-gray-800">{value || "—"}</dd>
  </div>
);

const CONTENT_CLASSES = LEGAL_CONTENT_CLASSES;

const LegalDocumentViewer = () => {
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();

  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await listPublishedLegalDocuments();
      setDocuments(docs);
    } catch {
      toast.error("Could not load legal documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const publishedByType = useMemo(() => {
    const map = new Map<LegalDocumentType, LegalDocument>();
    documents.forEach((doc) => {
      map.set(doc.documentType, doc);
    });
    return map;
  }, [documents]);

  const requestedParam = searchParams.get("type");
  const validRequested = LEGAL_DOCUMENT_TYPES.some((t) => t.value === requestedParam)
    ? (requestedParam as LegalDocumentType)
    : null;

  const [activeType, setActiveType] = useState<LegalDocumentType>(
    LEGAL_DOCUMENT_TYPES[0].value
  );
  useEffect(() => {
    if (validRequested) {
      setActiveType(validRequested);
      return;
    }
    const firstPublished = LEGAL_DOCUMENT_TYPES.find((t) =>
      publishedByType.has(t.value)
    );
    if (firstPublished) {
      setActiveType(firstPublished.value);
    }
  }, [validRequested, publishedByType]);

  const activeDoc = publishedByType.get(activeType);
  const activeLabel =
    LEGAL_DOCUMENT_TYPES.find((t) => t.value === activeType)?.label ?? activeType;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/admin"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors shrink-0"
              aria-label="Back to dashboard"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                <Scale size={18} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight truncate">
                  Legal Documents
                </h1>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider truncate">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>

          <Link
            to="/admin/legal/history"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors duration-200 shrink-0"
          >
            <History size={16} />
            <span>My Acceptance History</span>
          </Link>

          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 shrink-0"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Document type tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5 flex gap-1 overflow-x-auto">
          {LEGAL_DOCUMENT_TYPES.map((t) => {
            const isActive = t.value === activeType;
            const isAvailable = publishedByType.has(t.value);
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setActiveType(t.value)}
                className={`flex-1 min-w-[9rem] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <FileText
                  size={15}
                  className={isActive ? "text-white" : "text-gray-400"}
                />
                {t.label}
                {isAvailable ? (
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isActive ? "bg-white" : "bg-emerald-500"
                    }`}
                    aria-label="Published version available"
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading document...</p>
          </div>
        ) : activeDoc ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 sm:p-8">
              {/* Document heading */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 border-b border-gray-100 pb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck size={16} className="text-primary-600" />
                    <span className="text-xs font-semibold text-primary-700 uppercase tracking-wider">
                      {activeLabel}
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                    {activeDoc.title || activeLabel}
                  </h2>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Published
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-neutral-100 text-neutral-700 border border-neutral-200">
                    Version {formatVersionDisplay(activeDoc.version)}
                  </span>
                </div>
              </div>

              {/* Metadata */}
              <dl className="mt-5 grid grid-cols-2 gap-6">
                <MetaItem
                  label="Effective date"
                  value={formatDate(activeDoc.effectiveFrom)}
                />
                <MetaItem
                  label="Last updated"
                  value={
                    formatDate(activeDoc.updatedAt || activeDoc.publishedAt) ||
                    "—"
                  }
                />
              </dl>
            </div>

            {/* Content */}
            <div className="border-t border-gray-100 bg-gray-50/40 px-5 sm:px-8 py-6 sm:py-8">
              <div className="flex items-center gap-2 mb-3">
                <ScrollText size={15} className="text-gray-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Document content
                </span>
              </div>
              <div
                className={CONTENT_CLASSES}
                dangerouslySetInnerHTML={{ __html: activeDoc.content }}
              />
              <p className="mt-8 text-xs text-gray-400 border-t border-gray-200 pt-4">
                This is the currently published version of this document
                (v{formatVersionDisplay(activeDoc.version)}). Previous versions are retained by
                DiagnomIQ for compliance and are available on request.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="h-14 w-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
              <FileText size={22} className="text-gray-300" />
            </div>
            <p className="text-gray-500 text-base font-medium">
              No published version available
            </p>
            <p className="text-gray-400 text-sm mt-1">
              The {activeLabel} is not available yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalDocumentViewer;